import { effect, inject, Injectable, signal, untracked } from "@angular/core";
import { AuthServiceImpl } from "@azavista/planner-lib/lib/auth/auth-service-impl";
import { OnsiteCheckInClientService } from "./onsite-checkin-client.service";
import { IAuthCredentials, IAuthProvider, ITokenPayload, IUser, OnsiteCheckInClientConfig } from "@azavista/planner-lib";
import { createHttpClient } from "@azavista/planner-lib/lib/core/http";
import { AxiosInstance } from 'axios';
import { ConfigurationStorageService } from "../../configuration-storage.service";
import { environment } from "../../../../environments/environment";
import { delay } from "@azavista/components/shared";
import { axiosErrorHandler, OnsiteCheckInClientErrorService } from "./onsite-checkin-client-error.service";
import { Router } from "@angular/router";
import { OnsiteCheckInClientUtilService } from "./onsite-checkin-client.util.service";
import { CustomerChatbotService } from "../customer-chatbot/types";
import { AuthPersistenceImpl } from "@azavista/planner-lib/lib/auth/auth-persistence";
import { AzavistaError } from "@azavista/planner-lib/lib/core/error";
import { isLogicalError } from "./utils";

export type UpdatableApiConfig = Pick<OnsiteCheckInClientConfig, 'baseUrl' | 'clientId'>;

@Injectable({
    providedIn: 'root'
})
export class OnsiteCheckInClientAuthService {
    private router = inject(Router);
    private authSvc = inject(AuthServiceImpl);
    private authPersistenceSvc = inject(AuthPersistenceImpl);
    public get authSvcHttpClient(): AxiosInstance {
        return this.authSvc['httpClient'];
    }
    public set authSvcHttpClient(value: AxiosInstance) {
        this.authSvc['httpClient'] = value;
    }
    private clientSvc = inject(OnsiteCheckInClientService);
    private clientErrorSvc = inject(OnsiteCheckInClientErrorService);
    private clientUtilsSvc = inject(OnsiteCheckInClientUtilService);
    private configSvc = inject(ConfigurationStorageService);
    private chatbotSvc = inject(CustomerChatbotService);

    isAuthenticated$ = signal(false);
    user$ = signal<IUser | undefined>(undefined);
    tokenPayload$ = signal<ITokenPayload | undefined>(undefined);

    constructor () {
        this.init();
        this.initListener();
        effect(() => {
            const isAuthenticated = this.isAuthenticated$();
            const user = untracked(() => this.user$());
            const token = untracked(() => this.tokenPayload$());
            if (isAuthenticated) {
                this.chatbotSvc.init({
                    user,
                    token
                });
            }
        });
    }

    private async init() {
        try {
            const currentUrl = new URL(window.location.href);
            const refreshTokenFromQuery = currentUrl.searchParams.get('refresh_token');

            if (refreshTokenFromQuery) {
                await this.authenticateFromQueryToken(refreshTokenFromQuery);
            } else if (this.authPersistenceSvc.getAuthResponse()?.access_token) {
                await this.authSvc.refreshToken();
            }
        } catch(error) {
            const azavistaError = error as AzavistaError;
            if (azavistaError.cause && isLogicalError(azavistaError.cause)) {
                await this.logout();
            } else {
                this.clientErrorSvc.setError(error, {
                    caller: 'auth-initialization',
                }, true);
            }
        } finally {
            const token = this.authSvc.getTokenPayload();
            if (token?.u) {
                this.authSvc.init();
                await this.clientSvc.init({
                    baseUrl: this.configSvc.getAppConfig()?.apiServerBaseUrl || environment.apiServerBaseUrl,
                    token: token,
                    clientId: environment.clientId,
                });
                this.tokenPayload$.set(token);
                this.user$.set(this.authSvc.getCurrentUser());
            } else {
                this.navigateToLogin();
            }
            const isAuthenticated = this.authSvc.isAuthenticated();
            this.isAuthenticated$.set(isAuthenticated);
        }
    }

    private async authenticateFromQueryToken(refreshTokenFromQuery: string): Promise<void> {
        const url = new URL(window.location.href);
        this.useConfig(this.getUpdatedApiConfig());

        await this.authSvc.authenticate({
            grant_type: 'refresh_token',
            refresh_token: refreshTokenFromQuery
        });

        // Remove the refresh token from URL after we used it.
        url.searchParams.delete('refresh_token');
        history.replaceState({}, document.title, url.toString());
    }

    private initListener() {
        this.authSvc.onUserChange((user) => {
            this.user$.set(user);
            this.tokenPayload$.set(this.authSvc.getTokenPayload());
        });
        this.authSvc.onAuthStateChange(async (isAuthenticated) => {
            this.isAuthenticated$.set(isAuthenticated);
            if (!isAuthenticated) {
                await delay(500);
                if (this.clientSvc.isInit$.value) {
                    this.logout();
                }
            }
        });
    }

    navigateToLogin(returnUrl?: string): void {
        const loginRouterPath = '/login';
        const url = new URL(window.location.href);
        if (url.pathname === '/login') {
            // The URL will already navigate to login
            return;
        }
        if (!returnUrl || returnUrl.endsWith('/login')) {
            this.router.navigate([loginRouterPath]);
        } else {
            if (url.pathname === '/') {
                url.pathname = '/events';
            }
            const returnUrlParamName = 'returnUrl';
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let queryParams: any;
            if (url.searchParams.has(returnUrlParamName)) {
                // Already have the returnUrl parameter - do not add it again
                queryParams = url.searchParams.toString();
            } else {
                // The ULR does not have redirectUrl - add it
                const returnUrlValue = url.href;
                queryParams = { returnUrl: returnUrlValue };
            }
            this.router.navigate([loginRouterPath], { queryParams: queryParams });
        }
    }


    async authenticate(credentials: IAuthCredentials, config?: UpdatableApiConfig): Promise<void> {
        this.useConfig(config);
        await this.authSvc.authenticate(credentials);
        const initConfig: OnsiteCheckInClientConfig = {
            baseUrl: this.authSvcHttpClient.defaults.baseURL,
            token: this.authSvc.getTokenPayload(),
            clientId: this.authSvcHttpClient.defaults.clientId,
        };
        // if success then init the client instance
        await this.clientSvc.init(initConfig);
    }

    async scanQrCode(qrcode: string, config?: UpdatableApiConfig & {
        restorePreviousConfig?: boolean;
    }): Promise<string> {
        const currentConfig = this.getConfig();
        this.useConfig(config);
        return this.authSvc.scanQrCode(qrcode).catch(error => {
            if (config?.restorePreviousConfig) {
                this.useConfig(currentConfig);
            }
            throw error;
        });
    }

    sendAuthenticationPin(email: string, config?: UpdatableApiConfig ): Promise<void> {
        this.useConfig(config);
        return this.authSvc.sendAuthenticationPin(email);
    }

    sendPublicPasswordResetLink(email: string, config?: UpdatableApiConfig): Promise<void> {
        this.useConfig(config);
        return this.authSvc.sendPublicPasswordResetLink(email);
    }

    getAuthProviders(config?: UpdatableApiConfig): Promise<IAuthProvider[]> {
        this.useConfig(config);
        return this.authSvc.getAuthProviders();
    }

    async authenticateForEvent(eventId: string): Promise<void> {
        const config = this.getUpdatedApiConfig();
        const authResponse = this.authPersistenceSvc.getAuthResponse();

        await this.authenticate({
            grant_type: 'refresh_token_for_event',
            refresh_token: authResponse?.refresh_token || '',
            event_id: eventId,
        }, config);
    }

    getUpdatedApiConfig(): UpdatableApiConfig {
        const cfg = this.configSvc.getAppConfig();
        const apiServerBaseUrl =
            cfg.apiServerBaseUrl || environment.apiServerBaseUrl;
        return {
            baseUrl: apiServerBaseUrl,
            clientId: environment.clientId,
        };
    }

    private useConfig(config: Pick<OnsiteCheckInClientConfig, "baseUrl" | "clientId">) {
        if (config && !this.clientUtilsSvc.getIsHttpClientHasSameConfig(config, this.authSvcHttpClient)) {
            this.authSvcHttpClient = createHttpClient(
                config.baseUrl, config.clientId, error => axiosErrorHandler(error, this.clientErrorSvc, true)
            );
        }
    }

    private getConfig(): UpdatableApiConfig {
        return {
            baseUrl: this.authSvcHttpClient.defaults.baseURL,
            clientId: this.authSvcHttpClient.defaults.clientId || environment.clientId,
        };
    }

    async logout() {
        await this.authSvc.logout();
        this.clientSvc.destroy();
        this.chatbotSvc.endUserSession();
    }
}
